import {
  Fragment, MouseEvent as ReactMouseEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState
} from "react";
import {
  SelectProps, CSSObject, Theme, styled, List, Box, ClickAwayListener, MenuItem, MenuList, Paper, Popper,
  Skeleton, SxProps,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { CabComponentProps } from "../cabStyled";
import { CabNavItem, CabNavItemProps } from "./CabNavItem";
import colors from "../../../colors";
import { User } from "../../../store";
import { PassedComponent } from "../../../utils/types";
import { CabIcon } from "@CabComponents/CabIcon";
import { DndContext, useSensor, PointerSensor, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CabButton } from "..";
import { useMountEffect } from "../../../utils/hooks";
import { 
  IoCheckmarkCircleOutline, IoChevronBackCircleOutline, IoCloseCircleOutline,
  IoChevronDownOutline, IoLockClosedOutline, IoPersonCircleOutline, IoSettingsOutline,
} from "react-icons/io5";
import { IconType } from "react-icons";
import { LuArrowDownUp } from "react-icons/lu";
import { useWindowSize } from "react-use";
import { PAGE_URL } from "../../../constants";

const drawerWidth = 230;
const transitionDuration = 300;
const TEAM_GROUP_ID = 'TEAM_GROUP';

export interface SectionItem {
  title: string;
  locked?: boolean;
  hidden?: boolean;
  Icon?: IconType;
  iconSize?: number;
  iconStrokeWidth?: number;
  iconOverride?: PassedComponent<object>;
  IconRight?: IconType;
  secondaryIcon?: IconType;
  secondaryIconTooltip?: string;
  path?: string;
  onClick: () => void;
  onClickLocked?: () => void,
  onClickMobile?: () => void,
  childItems?: SectionItem[],
  id?: number;
  groupId?: string;
  sectionHeader?: boolean;
  sectionBreakBefore?: boolean;
  collapsedPopover?: boolean;
  alwaysExpanded?: boolean;
  alignBottom?: boolean;
  newIcon?: boolean;
  elementId?: string;
}

export interface Section {
  items: SectionItem[];
}

type DrawerSectionProps = {
  section: Section;
  showMore: boolean;
  sectionItemActive: (sectionItem: SectionItem) => boolean;
  handleSetOpen? : (value: boolean) => void;
  scrollable?: boolean;
  sx?: SxProps<Theme>;
};

type DrawerLeaderSectionProps = {
  section: Section;
  showMore: boolean;
  sectionItemActive: (sectionItem: SectionItem) => boolean;
  handleSetOpen? : (value: boolean) => void;
  sortable?: boolean;
  sortableLeaders: SectionItem[];
  setSortableLeaders: React.Dispatch<React.SetStateAction<SectionItem[]>>
};

export interface CabNavBarProps extends CabComponentProps<SelectProps> {
  logo: string;
  collapsedLogo: string;
  mainSection: Section;
  bottomSection?: Section;
  sectionItemActive: (sectionItem: SectionItem) => boolean;
  parentOpen?: boolean
  onOpen?: (value: boolean) => void
  user: User | null | undefined;
  onChangePage: (pageUrl: string, external?: boolean) => void;
  onLogout: (global?: boolean) => void;
  versionNumber: string;
  leadersLoaded: boolean;
  variant: "permanent" | "temporary";
  onTemporaryDrawerClick: (value: unknown) => void;
  onOpenAddLeader?: () => void;
  onReorderLeaders: (leaderIds: number[]) => Promise<void>
  newNotificationsCount: number;
}

export const CabNavBar = ({
  collapsedLogo, mainSection: mSection, bottomSection, sectionItemActive, parentOpen, onOpen,
  onOpenAddLeader, user,
  onChangePage, leadersLoaded, variant, onTemporaryDrawerClick, onReorderLeaders
}: CabNavBarProps): ReactElement => {
  const [localOpen, setLocalOpen] = useState(parentOpen || false);
  const [showMore, setShowMore] = useState(parentOpen || false);
  const [sortLeaders, setSortLeaders] = useState(false);
  const [leaders, setLeaders] = useState<SectionItem[]>([]);
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState<HTMLElement | null>(null);
  const accountMenuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { height } = useWindowSize();
  const scrollable = height < 580;

  const leaderSection = useMemo(() => {
    const lSection = mSection.items.find(item => item.groupId === TEAM_GROUP_ID);
    if (!lSection) return { items: [] };

    const sec = { ...lSection, items: [...(lSection.childItems || [])] };

    if (!sortLeaders) {
      sec.items = [...sec.items, {
        title: "Edit Order",
        Icon: LuArrowDownUp,
        onClick: () => setSortLeaders(true),
        locked: false,
        id: -2
      }];
    }

    return sec;
  }, [mSection.items, sortLeaders]);
  const teamSectionTitle = mSection.items.find(item => item.groupId === TEAM_GROUP_ID)?.title || 'Teammates';

  const mainSection: Section = useMemo(() => {
    if (sortLeaders) {
      return {
        items: mSection.items.filter(item => item.groupId !== TEAM_GROUP_ID && !item.alignBottom)
      };
    }

    return {
      items: mSection.items.map(item => 
        item.groupId === TEAM_GROUP_ID
          ? { ...item, ...leaderSection, childItems: leaderSection.items }
          : item
      ).filter(item => !item.alignBottom)
    };
  }, [sortLeaders, mSection.items, leaderSection]);

  const footerSection: Section = useMemo(() => ({
    items: mSection.items.filter(item => item.alignBottom)
  }), [mSection.items]);

  useEffect(() => {
    setLeaders(leaderSection.items.filter(item => item.id !== -1));
  }, [leaderSection.items]);

  const cancelEditOrder = useCallback(() => {
    setLeaders(leaderSection.items.filter(item => item.id !== -1));
    setSortLeaders(false);
  }, [leaderSection.items]);

  const handleSetOpen = useCallback((value: boolean): void => {
    if (onOpen) {
      onOpen(value);
    }
    setLocalOpen(value);
  }, [onOpen]);

  useEffect(() => {
    if (localOpen && !showMore) {
      setTimeout(() => {
        setShowMore(true);
      }, transitionDuration - 200);
    } else if (!localOpen && showMore) {
      setShowMore(false);
    }
    // we only want to run this effect when localOpen changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localOpen]);

  useEffect(() => {
    if (!localOpen) {
      if (sortLeaders) {
        cancelEditOrder();
      }
    }
  }, [cancelEditOrder, localOpen, sortLeaders]);

  const handleDrawerOpen = useCallback(() => {
    setAccountMenuAnchorEl(null);
    handleSetOpen(true);
  }, [handleSetOpen]);

  const handleDrawerClose = useCallback(() => {
    setAccountMenuAnchorEl(null);
    handleSetOpen(false);
  }, [handleSetOpen]);

  useEffect(() => {
    if (parentOpen) {
      handleDrawerOpen();
    } else {
      handleDrawerClose();
    }
  }, [handleDrawerOpen, handleDrawerClose, parentOpen]);

  const saveLeaderOrder = () => {
    const leaderIds = leaders.map(leader => leader.id || -1);
    const filterIds = leaderIds.filter(id => id !== -1);
    onReorderLeaders(filterIds);
    setSortLeaders(false);
  };

  const sortModeLeaderContent = (
    <Box sx={{
      direction: 'ltr',
      minHeight: 80 * leaders.length,
      paddingBottom: '88px',
    }}>
      {leadersLoaded
        ? <DrawerLeaderSection
          key={"executives"}
          section={leaderSection}
          showMore={localOpen}
          sectionItemActive={sectionItemActive}
          sortable={sortLeaders}
          sortableLeaders={leaders}
          setSortableLeaders={setLeaders}
        />
        : <>
          <LeaderSkeleton variant="rectangular" />
          <LeaderSkeleton variant="rectangular" />
          <LeaderSkeleton variant="rectangular" />
        </>
      }
    </Box>
  );

  const showExpandedLogo = localOpen && showMore;
  const userFullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Account';
  const accountMenuOpen = Boolean(accountMenuAnchorEl);
  const supportMapItem = bottomSection?.items.find(item => item.path === PAGE_URL.SUPPORT_MAP);

  const handleAccountMenuToggle = (event: ReactMouseEvent<HTMLElement>) => {
    setAccountMenuAnchorEl(accountMenuOpen ? null : event.currentTarget);
  };

  const handleAccountMenuClose = () => setAccountMenuAnchorEl(null);

  const keepAccountMenuOpen = () => {
    if (accountMenuCloseTimer.current) {
      clearTimeout(accountMenuCloseTimer.current);
      accountMenuCloseTimer.current = null;
    }
  };

  const openCollapsedAccountMenu = (anchorEl: HTMLElement) => {
    if (!localOpen) {
      keepAccountMenuOpen();
      setAccountMenuAnchorEl(anchorEl);
    }
  };

  const scheduleCollapsedAccountMenuClose = () => {
    keepAccountMenuOpen();
    accountMenuCloseTimer.current = setTimeout(() => {
      setAccountMenuAnchorEl(null);
    }, 150);
  };

  useEffect(() => () => {
    if (accountMenuCloseTimer.current) {
      clearTimeout(accountMenuCloseTimer.current);
    }
  }, []);

  const handleSupportMapClick = () => {
    handleAccountMenuClose();
    if (supportMapItem?.locked) {
      supportMapItem.onClickLocked?.();
    } else {
      supportMapItem?.onClick();
    }
  };

  const handleSettingsClick = () => {
    handleAccountMenuClose();
    onTemporaryDrawerClick(onChangePage(PAGE_URL.SETTINGS));
  };

  const handleMyAccountClick = () => {
    handleAccountMenuClose();
    onTemporaryDrawerClick(onChangePage(PAGE_URL.MY_ACCOUNT));
  };

  return (
    <Drawer
      variant={variant}
      anchor="left"
      open={localOpen}
      scrollable={scrollable}
      leaderSortMode={sortLeaders}
      transitionDuration={transitionDuration}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' && variant === 'temporary') {
          handleDrawerClose();
        }
      }}
    >
      <Box sx={{
        direction: 'ltr',
        ...(!scrollable ? {
          height: '100%',
          paddingBottom: 0,
          flex: 1,
          minHeight: 0,
        } : { minHeight: '100%' }),
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <DrawerHeader
          open={showExpandedLogo}
          sx={localOpen ? {
            padding: 1.75,
            paddingTop: 1.55,
            justifyContent: 'flex-start'
          } : undefined}
        >
          {showExpandedLogo ? (
            <>
              <Box
                component='button'
                type='button'
                aria-label={`${userFullName} account menu`}
                aria-expanded={accountMenuOpen}
                aria-haspopup='menu'
                onClick={handleAccountMenuToggle}
                sx={{
                  alignItems: 'center',
                  background: 'none',
                  border: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  font: 'inherit',
                  gap: 1,
                  maxWidth: '100%',
                  minWidth: 0,
                  padding: 0,
                  width: '100%',
                }}
              >
                <Box
                  component='img'
                  src={collapsedLogo}
                  sx={{
                    flexShrink: 0,
                    width: 30,
                    height: 'auto',
                  }}
                />
                <Box
                  component='span'
                  sx={{
                    color: colors.calmGrey800,
                    fontSize: 14,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userFullName}
                </Box>
                <CabIcon
                  Icon={IoChevronDownOutline}
                  alt='Account menu'
                  sx={{
                    color: colors.calmGrey500,
                    flexShrink: 0,
                    fontSize: 14,
                    marginLeft: 'auto',
                    transform: accountMenuOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 150ms ease',
                  }}
                />
              </Box>
            </>
          ) : (
            <Box
              component='img'
              src={collapsedLogo}
              alt='Cabinet'
              onPointerEnter={(event) => openCollapsedAccountMenu(event.currentTarget)}
              onPointerLeave={scheduleCollapsedAccountMenuClose}
              sx={{
                width: 30,
                height: 'auto',
                marginLeft: 0.75,
                marginTop: 0,
              }}
            />
          )}
        </DrawerHeader>
        <Popper
          open={accountMenuOpen}
          anchorEl={accountMenuAnchorEl}
          placement={localOpen ? 'bottom-start' : 'right-start'}
          sx={{ zIndex: theme => theme.zIndex.modal }}
        >
          <ClickAwayListener onClickAway={handleAccountMenuClose}>
            <Paper
              elevation={4}
              onMouseEnter={!localOpen ? keepAccountMenuOpen : undefined}
              onMouseLeave={!localOpen ? scheduleCollapsedAccountMenuClose : undefined}
              sx={{
                border: `1px solid ${colors.black200}`,
                borderRadius: 1.5,
                ...(localOpen ? { marginTop: 0.5 } : { marginLeft: 1 }),
                minWidth: 190,
                paddingY: 0.75,
              }}
            >
              <MenuList aria-label='Account menu' disablePadding>
                <Box
                  component='li'
                  role='presentation'
                  sx={{
                    color: colors.calmGrey400,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    listStyle: 'none',
                    paddingBottom: 0.5,
                    paddingLeft: 2,
                    paddingRight: 2,
                    paddingTop: 1,
                  }}
                >
                  {userFullName}
                </Box>
                <MenuItem
                  onClick={handleMyAccountClick}
                  sx={{ gap: 1.25, fontSize: 14, minHeight: 38, paddingX: 2 }}
                >
                  <CabIcon Icon={IoPersonCircleOutline} alt='My Account' size={18} />
                  My Account
                </MenuItem>
                {supportMapItem && !supportMapItem.hidden && (
                  <MenuItem
                    onClick={handleSupportMapClick}
                    sx={{ gap: 1.25, fontSize: 14, minHeight: 38, paddingX: 2 }}
                  >
                    {supportMapItem.Icon && (
                      <CabIcon Icon={supportMapItem.Icon} alt={supportMapItem.title} size={18} />
                    )}
                    <Box component='span' sx={{ flex: 1 }}>{supportMapItem.title}</Box>
                    {supportMapItem.locked && (
                      <CabIcon Icon={IoLockClosedOutline} alt='Locked' size={14} />
                    )}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={handleSettingsClick}
                  sx={{ gap: 1.25, fontSize: 14, minHeight: 38, paddingX: 2 }}
                >
                  <CabIcon Icon={IoSettingsOutline} alt='Settings' size={18} />
                  Settings
                </MenuItem>
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
        <Box sx={
          sortLeaders && !scrollable
            ? {
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              height: '100%',
            }
            : sortLeaders && scrollable
              ? {
                minHeight: 0,
                paddingBottom: localOpen ? '160px' : '180px',
              }
              : scrollable
                ? { minHeight: 0, paddingBottom: localOpen ? '160px' : '180px' }
                : { flex: 1, minHeight: 0, overflow: 'hidden' }
        }>
          <DrawerSection
            key={"main"}
            section={mainSection}
            showMore={localOpen}
            sectionItemActive={sectionItemActive}
            handleSetOpen={handleSetOpen}
            scrollable={!scrollable}
            sx={sortLeaders ? {
              height: 'auto',
              overflowY: 'hidden',
              paddingBottom: 0,
              ...(!scrollable ? { flexShrink: 0 } : {}),
            } : {}}
          />
          {sortLeaders && (
            <>
              <Box sx={{
                minHeight: 0,
              }}>
                {localOpen && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      color: colors.calmGrey400,
                      display: 'flex',
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      paddingBottom: 0.75,
                      paddingLeft: 2,
                      paddingTop: 3,
                    }}
                  >
                    {teamSectionTitle}
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    paddingLeft: 2,
                    paddingRight: 2,
                    paddingTop: 1,
                    paddingBottom: 1,
                    gap: 1,
                    flexShrink: 0
                  }}>
                  <CabButton buttonType='tertiary' color='primary' onClick={cancelEditOrder}
                    icon={<CabIcon alt='Delete' Icon={IoCloseCircleOutline} />} 
                    size="small" sx={{width: '50%', height: '26px'}}>
                    Cancel
                  </CabButton>
                  <CabButton buttonType='primary' color='primary' onClick={saveLeaderOrder}
                    icon={<CabIcon alt='Save' Icon={IoCheckmarkCircleOutline}/>} 
                    size="small" sx={{width: '50%', height: '26px'}}>
                    Save
                  </CabButton>
                </Box>

                {sortModeLeaderContent}
              </Box>
            </>
          )}
        </Box>
        <Box sx={{
          backgroundColor: colors.systemWhite,
          flexShrink: 0,
          marginTop: 'auto',
          ...(scrollable ? {
            bottom: 0,
            left: 0,
            position: 'fixed',
            width: localOpen ? drawerWidth : 68,
          } : {
            position: 'relative',
          }),
          zIndex: 1,
        }}>
          {!localOpen && footerSection.items.length > 0 && (
            <DrawerSection
              key="footer"
              section={footerSection}
              showMore={localOpen}
              sectionItemActive={sectionItemActive}
              handleSetOpen={handleSetOpen}
              scrollable={false}
              sx={{
                paddingBottom: 0,
                paddingTop: 0,
              }}
            />
          )}
          {localOpen && footerSection.items.length > 1 && (
            <DrawerSection
              key="expanded-footer-stack"
              section={{ items: footerSection.items.slice(0, -1) }}
              showMore={localOpen}
              sectionItemActive={sectionItemActive}
              handleSetOpen={handleSetOpen}
              scrollable={false}
              sx={{
                paddingBottom: 0,
                paddingTop: 0,
              }}
            />
          )}
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexShrink: 0,
              justifyContent: localOpen ? 'flex-end' : 'center',
              paddingBottom: 1.75,
              paddingLeft: localOpen ? 0 : 1.75,
              paddingRight: 1.75,
              paddingTop: localOpen ? 0 : 1,
            }}
          >
            {localOpen && footerSection.items.length > 0 && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <DrawerSection
                  key="expanded-footer-bottom"
                  section={{ items: [footerSection.items[footerSection.items.length - 1]] }}
                  showMore={localOpen}
                  sectionItemActive={sectionItemActive}
                  handleSetOpen={handleSetOpen}
                  scrollable={false}
                  sx={{
                    paddingBottom: 0,
                    paddingTop: 0,
                  }}
                />
              </Box>
            )}
            <DrawerTriggerIcon
              open={localOpen}
              Icon={IoChevronBackCircleOutline}
              color='primary'
              alt={localOpen ? 'collapse' : 'expand'}
              onClick={localOpen ? handleDrawerClose : handleDrawerOpen}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: colors.systemWhite,
  paddingTop: 'env(safe-area-inset-top)',
  border: 0,
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: theme.spacing(8.5),
  backgroundColor: colors.systemWhite,
  paddingTop: 'env(safe-area-inset-top)',
  border: 0,
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
});

const DrawerHeader = styled(
  'div', { shouldForwardProp: (prop) => prop !== 'open', label: "DrawerHeader" }
)<{ open?: boolean }>(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingRight: 3,
  paddingTop: 2,
  height: 64,
}));

const DrawerTriggerIcon = styled(
  CabIcon, { shouldForwardProp: (prop) => prop !== 'open', label: "DrawerTriggerIcon" }
)<{ open: boolean }>(({ open }) => ({
  ...(open ? {} : { transform: 'rotate(180deg)' }),
  fontSize: "24px",
  '&:hover': {
    cursor: 'pointer'
  }
}));

const LeaderSkeleton = styled(Skeleton, { label: "LeaderSkeleton" })(() => ({
  margin: 12,
  height: 32,
  borderRadius: 8
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'scrollable' && prop !== 'leaderSortMode',
  label: "Drawer",
})<{ scrollable?: boolean; leaderSortMode?: boolean }>(
  ({ theme, open, variant, scrollable, leaderSortMode }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    direction: 'rtl',
    '& .MuiPaper-root': {
      overflowY: scrollable ? 'auto' : 'hidden',
      ...(leaderSortMode && !scrollable && open ? {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      } : {}),
      '::-webkit-scrollbar': {
        width: open ? 8 : 4,
        height: 8,
        backgroundColor: 'none',
      },
      '::-webkit-scrollbar-thumb': {
        background: colors.black500,
        borderRadius: open ? 4 : 2,
      }
    },
    boxSizing: 'border-box',
    ...(open && {
      ...(variant === 'permanent' && openedMixin(theme)),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...(variant === 'permanent' && closedMixin(theme)),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const DrawerSection = ({ 
  section, showMore, sectionItemActive, handleSetOpen, scrollable, sx
}: DrawerSectionProps): ReactElement => {
  const [uncollapseAll, setUncollapseAll] = useState(false);

  return (
    <List sx={{ 
      paddingTop: 1.5,
      height: scrollable ? '100%' : 'auto',
      overflowY: scrollable ? 'auto' : 'hidden',
      ...(scrollable ? { paddingBottom: 0, boxSizing: 'border-box' as const } : {}),
      '::-webkit-scrollbar': {
        width: showMore ? 8 : 4,
        height: 8,
        backgroundColor: 'none',
      },
      '::-webkit-scrollbar-thumb': {
        background: colors.black500,
        borderRadius: showMore ? 4 : 2,
      },
      ...sx
    }}>
      {section.items.map((sectionItem) => {
        const childItems: CabNavItemProps[] = sectionItem.childItems?.map(((item: Partial<SectionItem>) => {
          const text = item.title;
          const key = item.path || item.title;
          return {
            ...item,
            open: showMore,
            text,
            key,
            sortId: item.id,
            active: false,
          } as CabNavItemProps;
        })) || [];

        if (sectionItem.sectionHeader) {
          const sectionActions = childItems.filter(childItem => (childItem.sortId || 0) < 0);
          const visibleChildItems = childItems.filter(childItem => (childItem.sortId || 0) >= 0);

          return (
            <Fragment key={sectionItem.title}>
              {showMore && (
                <Box
                  component='li'
                  sx={{
                    alignItems: 'center',
                    color: colors.calmGrey400,
                    display: 'flex',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    listStyle: 'none',
                    paddingBottom: 0.75,
                    paddingLeft: 2,
                    paddingTop: 3,
                  }}
                >
                  {sectionItem.title}
                </Box>
              )}
              {visibleChildItems.map(childItem => (
                <CabNavItem
                  {...childItem}
                  key={childItem.key}
                  active={sectionItemActive({
                    title: childItem.text,
                    path: childItem.path,
                    onClick: childItem.onClick || (() => undefined),
                  })}
                />
              ))}
              {showMore && sectionActions.map(sectionAction => (
                <CabNavItem
                  {...sectionAction}
                  key={sectionAction.key}
                  active={false}
                  open={showMore}
                />
              ))}
            </Fragment>
          );
        }

        return <CabNavItem
          open={showMore}
          key={sectionItem.path || sectionItem.title}
          sx={sectionItem.sectionBreakBefore ? { marginTop: 3 } : undefined}
          text={sectionItem.title}
          active={sectionItemActive(sectionItem)}
          Icon={sectionItem.Icon}
          iconSize={sectionItem.iconSize}
          iconStrokeWidth={sectionItem.iconStrokeWidth}
          IconRight={sectionItem.IconRight}
          secondaryIcon={sectionItem.secondaryIcon}
          secondaryIconTooltip={sectionItem.secondaryIconTooltip}
          iconOverride={sectionItem.iconOverride}
          locked={sectionItem.locked}
          hidden={sectionItem.hidden}
          onClick={sectionItem.onClick}
          onClickLocked={sectionItem.onClickLocked}
          onClickMobile={sectionItem.onClickMobile}
          childItems={childItems}
          handleNavbarDrawer={handleSetOpen}
          newIcon={sectionItem.newIcon}
          collapsedPopover={sectionItem.collapsedPopover}
          alwaysExpanded={sectionItem.alwaysExpanded}
          elementId={sectionItem.elementId}
          uncollapseAll={uncollapseAll}
          setUncollapseAll={setUncollapseAll}
        />;
      })}
    </List>
  );
};

const DrawerLeaderSection = ({ 
  section, showMore, sectionItemActive, handleSetOpen, sortable, sortableLeaders, setSortableLeaders 
}: DrawerLeaderSectionProps): ReactElement => {
  
  const sensors = useSensor(PointerSensor);
  useMountEffect(() => {
    setSortableLeaders(section.items.filter(item => item.id !== -1));
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (over && active.id !== over.id) {
      setSortableLeaders((items) => {
        const oldIdx = items.findIndex( item => item.id === active.id);
        const newIdx = items.findIndex( item => item.id === over.id);

        return arrayMove(items, oldIdx, newIdx);
      });
    }
  };

  return (
    !sortable ? (
      <>
        <List>
          {section.items.map((sectionItem) => {
            const childItems: CabNavItemProps[] = sectionItem.childItems?.map(((item: Partial<SectionItem>) => {
              const text = item.title;
              const key = item.path || item.title;
              return {
                ...item,
                open: showMore,
                text,
                key,
                active: false,
              } as CabNavItemProps;
            })) || [];

            return <CabNavItem
              open={showMore}
              key={sectionItem.path || sectionItem.title}
              text={sectionItem.title}
              active={sectionItemActive(sectionItem)}
              Icon={sectionItem.Icon}
              iconSize={sectionItem.iconSize}
              iconStrokeWidth={sectionItem.iconStrokeWidth}
              IconRight={sectionItem.IconRight}
              iconOverride={sectionItem.iconOverride}
              locked={sectionItem.locked}
              hidden={sectionItem.hidden}
              onClick={sectionItem.onClick}
              onClickLocked={sectionItem.onClickLocked}
              onClickMobile={sectionItem.onClickMobile}
              childItems={childItems}
              handleNavbarDrawer={handleSetOpen}
            />;
          })}
        </List>
      </>
    ) : (
      <DndContext
        sensors={[sensors]}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={sortableLeaders.map(item => item.id || -1)}
          strategy={verticalListSortingStrategy}
        >
          <List>
            {sortableLeaders.map((sectionItem) => {
              const childItems: CabNavItemProps[] = sectionItem.childItems?.map(((item: Partial<SectionItem>) => {
                const text = item.title;
                const key = item.path || item.title;
                return {
                  ...item,
                  open: showMore,
                  text,
                  key,
                  active: false,
                } as CabNavItemProps;
              })) || [];

              return <CabNavItem
                open={showMore}
                key={sectionItem.path || sectionItem.title}
                text={sectionItem.title}
                active={sectionItemActive(sectionItem)}
                Icon={sectionItem.Icon}
                iconSize={sectionItem.iconSize}
                iconStrokeWidth={sectionItem.iconStrokeWidth}
                IconRight={sectionItem.IconRight}
                iconOverride={sectionItem.iconOverride}
                locked={sectionItem.locked}
                hidden={sectionItem.hidden}
                onClick={sectionItem.onClick}
                onClickLocked={sectionItem.onClickLocked}
                onClickMobile={sectionItem.onClickMobile}
                childItems={childItems}
                handleNavbarDrawer={handleSetOpen}
                sortId={sectionItem.id}
                sortable={sortable}
              />;
            })}
          </List>
        </SortableContext>
      </DndContext>
    )
  );
};
